import useSWR from 'swr';
import * as moviesAPI from '../api/movies';

/**
 * SWR Fetcher wrapper to isolate cache key from API function args
 * SWR passes the cache key as first argument, but our API functions have their own args
 */
function createFetcher<T>(fn: () => Promise<T>): (key: string) => Promise<T> {
  return async () => fn();
}

function createFetcherWithArg<T, A>(
  fn: (arg: A) => Promise<T>,
  arg: A,
): (key: string) => Promise<T> {
  return async () => fn(arg);
}

function createFetcherWithTwoArgs<T, A, B>(
  fn: (arg1: A, arg2: B) => Promise<T>,
  arg1: A,
  arg2: B,
): (key: string) => Promise<T> {
  return async () => fn(arg1, arg2);
}

/**
 * SWR configuration for movie data caching
 * Provides automatic revalidation, caching, and request deduplication
 * Reduces redundant API calls and improves perceived performance with instant cache hits
 */
const swrConfig = {
  // Cache data for 30 seconds before considering it stale
  dedupingInterval: 30000,
  // Revalidate data in the background after 60 seconds of no focus
  focusThrottleInterval: 60000,
  // Automatically revalidate when user re-focuses the window/tab
  revalidateOnFocus: true,
  // Automatically revalidate when user goes from offline to online
  revalidateOnReconnect: true,
  // Always fetch on initial mount so data loads on first render
  revalidateOnMount: true,
};

/**
 * Hook for fetching upcoming movies by region
 * Automatically caches results and provides stale-while-revalidate behavior
 * @param region - Movie region code (e.g., 'US', 'BR')
 * @returns { data, error, isLoading, mutate }
 */
export const useUpcoming = (region: string) => {
  const resolvedRegion = region ?? '';
  const url = `/api/upcoming/${resolvedRegion}`;
  const { data, error, isLoading, mutate } = useSWR(
    url,
    createFetcherWithArg(() => moviesAPI.getUpcoming(region), region),
    swrConfig,
  );

  return {
    movies: data,
    isLoading,
    error,
    mutate,
  };
};

/**
 * Hook for user's ratings or watchlist
 * Caches per username, automatically invalidates cache keys
 * @param username - Username to fetch data for
 * @param getRatings - If true, fetch ratings; otherwise fetch watchlist
 * @returns { data, error, isLoading, mutate }
 */
export const useRatingsOrWatchlist = (
  username: string,
  getRatings: boolean = true,
) => {
  const url = username
    ? `/api/u/${username}/${getRatings ? 'ratings' : 'watchlist'}`
    : null;
  const { data, error, isLoading, mutate } = useSWR(
    url,
    createFetcherWithTwoArgs(
      moviesAPI.getRatingsOrWatchlist,
      username,
      getRatings,
    ),
    swrConfig,
  );

  return {
    movies: data,
    isLoading,
    error,
    mutate,
  };
};

/**
 * Hook for searching movies
 * Caches search results per query, deduplicated by SWR
 * @param query - Search query string
 * @returns { data, error, isLoading, mutate }
 */
export const useSearch = (query: string) => {
  const url =
    query && query.trim().length > 0
      ? `/api/search/${encodeURIComponent(query)}`
      : null;
  const { data, error, isLoading, mutate } = useSWR(
    url,
    createFetcherWithArg(moviesAPI.search, query),
    swrConfig,
  );

  return {
    results: data,
    isLoading,
    error,
    mutate,
  };
};

/**
 * Hook for fetching latest activity
 * Minimal cache time (10s) since activity changes frequently
 * @returns { data, error, isLoading, mutate }
 */
export const useLatestActivity = () => {
  const { data, error, isLoading, mutate } = useSWR(
    '/api/activity/',
    createFetcher(() => moviesAPI.getLatestActivity()),
    {
      ...swrConfig,
      dedupingInterval: 10000, // Shorter cache for activity
    },
  );

  return {
    activity: data,
    isLoading,
    error,
    mutate,
  };
};

/**
 * Hook for fetching all users
 * Rarely changes, so cached for longer (5 minutes)
 * @returns { data, error, isLoading, mutate }
 */
export const useAllUsers = () => {
  const { data, error, isLoading, mutate } = useSWR(
    '/api/users/',
    createFetcher(() => moviesAPI.getAllUsers()),
    {
      ...swrConfig,
      dedupingInterval: 5 * 60 * 1000, // 5 minute cache
    },
  );

  return {
    users: data,
    isLoading,
    error,
    mutate,
  };
};

/**
 * Hook for fetching a specific movie by ID
 * @param id - Movie ID
 * @param extraInfo - Comma-separated list of extra fields to fetch from TMDB
 * @returns { data, error, isLoading, mutate }
 */
export const useMovieById = (id: string, extraInfo?: string) => {
  const url = id ? `/api/movieById/${id}/${extraInfo || ''}` : null;
  const { data, error, isLoading, mutate } = useSWR(
    url,
    createFetcherWithArg(
      (args: { id: string; extraInfo: string }) =>
        moviesAPI.getMovieById(args.id, false, args.extraInfo),
      { id, extraInfo: extraInfo || '' },
    ),
    swrConfig,
  );

  return {
    movie: data,
    isLoading,
    error,
    mutate,
  };
};

/**
 * Hook for fetching movies from a person (actor/director)
 * @param personId - TMDB person ID
 * @returns { data, error, isLoading, mutate }
 */
export const useMoviesFromPerson = (personId: string) => {
  const url = personId ? `/api/p/${personId}` : null;
  const { data, error, isLoading, mutate } = useSWR(
    url,
    createFetcherWithArg(moviesAPI.getMoviesFromPerson, personId),
    swrConfig,
  );

  return {
    movies: data,
    isLoading,
    error,
    mutate,
  };
};

/**
 * Hook for user options
 * Minimal cache time since preferences change frequently
 * @returns { data, error, isLoading, mutate }
 */
export const useUserOptions = () => {
  const { data, error, isLoading, mutate } = useSWR(
    '/api/user/options',
    createFetcher(() => moviesAPI.getUserOptions()),
    {
      ...swrConfig,
      dedupingInterval: 10000, // Shorter cache for user options
    },
  );

  return {
    options: data,
    isLoading,
    error,
    mutate,
  };
};

/**
 * Utility function to invalidate all movie-related caches
 * Useful after mutations (rating, watchlist changes)
 */
export const invalidateMovieCaches = () => {
  // This would need to be called with a mutate function from useSWRConfig
  // Or use SWR's unstable_serialize to clear specific caches
  // For now, this is a placeholder for future implementation
};
